/**
 * Components
 */
import popup from '../../components/popup'
import dropDown from '../../components/dropDown'
import errorPage from '../../components/errorPage'
import offersList from '../../components/offersList'
import offersHeader from '../../components/offersHeader'
import dateSelector from '../../components/dateSelector'
import dropdownPaxSelector from '../../components/dropdownPaxSelector'
import typeStage from '../../components/typeStage'
import linkedTypeStage from '../../components/linkedTypeStage'
import venueStage from '../../components/venueStage'
import timeStage from '../../components/timeStage'
import multiTimeStage from '../../components/multiTimeStage'
import detailsStage from '../../components/detailsStage'
import altStage from '../../components/altStage'
import confirmationStage from '../../components/confirmationStage'

/**
 * Services
 */
import DateService from '../../services/DateService'
import StageManager from '../../managers/StageManager'
import PromptService from '../../services/PromptService'
import WindowManager from '../../managers/WindowManager'
import AnalyticsService from '../../services/AnalyticsService'

/**
 * Packages
 */
import { Source } from '../../models/Source'
import {BookingTime} from '../../services/TimesProvider'
import GoogleTagManagerService from '../../services/GoogleTagManagerService'

export default {
  name: 'partnerWidget',
  components: {
    popup,
    dropDown,
    typeStage,
    linkedTypeStage,
    errorPage,
    timeStage,
    multiTimeStage,
    venueStage,
    offersList,
    altStage,
    detailsStage,
    offersHeader,
    confirmationStage,
    dateSelector,
    dropdownPaxSelector
  },

  data () {
    return {
      options: {},
      poweredBy: true,
      offerDrawerOpen: false,
      shouldShowOffers: false,

      // https://github.com/designmynight/dmn/issues/28217
      shouldRedirect: true,

      stageManager: {
        getStageById () {
          return {}
        }
      }
    }
  },
  methods: {

    toggleOffers () {
      if (this.shouldShowOffers) {
        this.shouldShowOffers = false
        return
      }

      this.analyticsService.trackEvent('Booking Widget v2', 'Show Available Offers', this.$root.booking.venue.name)

      this.shouldShowOffers = true
    },

    setStageTextForVenue () {
      const venue = this.$root.booking.venue

      if (venue === null) {
        this.stageManager.setStageText(0, this.$t('venue_stage.header'))
        return
      }

      this.stageManager.setStageText(0, venue.name)
      return
    },

    setStageTextForPeopleAndDate () {
      const people = this.$root.booking.pax
      const date = this.$root.booking.date

      if (date === null) {
        this.stageManager.setStageText(1, this.$t('date_and_pax_stage.header'))// 'Select group number & date below')
        return
      }

      const text = people + ' ' + this.$tc('date_and_pax_stage.number_suffix', people) + ', ' + DateService.friendlyDate(date)

      this.stageManager.setStageText(1, text)
      return
    },

    setStageTextForType () {
      const type = this.$root.booking.type

      if (type === null) {
        this.stageManager.setStageText(2, this.$t('booking_type_stage.header'))
        return
      }

      let text = type.name

      if (this.$root.additionalTypes && this.$root.additionalTypes.length) {
        this.$root.additionalTypes.forEach((additionalType) => {
          text += ', ' + additionalType.name
        })
      }

      this.stageManager.setStageText(2, text)
    },

    setStageTextForTimes () {
      const times = this.$root.booking.times

      if (times === null) {
        this.stageManager.setStageText(3, this.$t('time_stage.header'))
        return
      }

      const fromTime = times.getFriendlyStartTime()
      const endTime = times.getFriendlyEndTime()

      this.stageManager.setStageText(3, fromTime + ' - ' + endTime)
    },

    venueSet (venue) {
      this.setStageTextForVenue()

      this.analyticsService.emitEvent('setVenue')

      if (this.showBookingTypeFirst) {
        this.goToTypeStage()
      } else {
        this.goToDateStage()
      }
    },

    dateSet (date) {
      this.$root.booking.pax = this.$refs.paxSelector.requestValue()
      this.setStageTextForPeopleAndDate()

      this.analyticsService.emitEvent('setDateNumberPeople')

      if (this.showBookingTypeFirst) {
        this.goToTimeStage()
      } else {
        this.goToTypeStage()
      }
    },

    typeSet (type) {
      this.setStageTextForType()

      this.analyticsService.emitEvent('setBookingType')

      this.gtmService.trackViewBookingType(type, this.$root.booking)

      if (type.isLinkedType && type.linkedTypes && type.linkedTypes.length) {
        // If a linked type itself has been selected (as a combo package),
        // then set the selected type to the first one in the list, and the remaining types as additional linked types
        this.$root.booking.type = type.linkedTypes[0][0]
        this.$root.additionalTypes = type.linkedTypes[0].slice(1)

        // Go straight to time stage
        this.goToMultiTimeStage()
      } else if (this.$root.dmnOptions.linkedBookingsEnabled && type.linkedTypes && type.linkedTypes.length) {
        this.goToLinkedTypesStage()
      } else if (this.showBookingTypeFirst) {
        this.goToDateStage()
      } else {
        this.goToTimeStage()
      }
    },

    additionalTypesSet (types) {
      this.$root.additionalTypes = types.slice()

      this.setStageTextForType()

      // Don't show this stage in the bread-crumb history
      this.stageManager.getStageById(8).open = false
      this.stageManager.getStageById(8).visible = false

      if (this.$root.additionalTypes && this.$root.additionalTypes.length) {
        this.goToMultiTimeStage()
      } else {
        this.goToTimeStage()
      }
    },

    timeSet (time) {
      this.setStageTextForTimes()

      this.analyticsService.emitEvent('setTime')
      this.gtmService.trackSelectBookingType(
        this.$root.booking.type,
        this.$root.booking
      )

      if (this.shouldRedirect) {
        this.openModal()
      } else {
        this.goToDetailsStage()
      }
    },

    multiTimeSet (times) {
      // Loop over every submitted MultiTimeBookingDetails object and extract relevant info
      times.forEach((timeDetails) => {
        // Check if the booking type matches the main booking
        if (timeDetails.id === this.$root.booking.type.id) {
          // Update main booking's time and duration
          this.$root.booking.times = new BookingTime(
            timeDetails.getSelectedTime(),
            timeDetails.duration
          )
          return
        }

        // Otherwise find the type in the list of additionalTypes
        this.$root.additionalTypes.forEach((additionalType) => {
          if (timeDetails.id === additionalType.id) {
            additionalType.startTime = timeDetails.getSelectedTime()
            additionalType.duration = timeDetails.duration
          }
        })
      })

      this.analyticsService.emitEvent('setTime')
      this.gtmService.trackSelectBookingType(
        this.$root.booking.type,
        this.$root.booking
      )

      if (this.shouldRedirect) {
        this.openModal()
      } else {
        this.goToDetailsStage()
      }
    },

    bounceContent (refName) {
      const ref = this.$refs[refName]
      const contentElm = ref.$el || ref

      clearTimeout(this.bounceTimeout)

      contentElm.classList.add('subtle-bounce-in-active')

      this.bounceTimeout = setTimeout(() => {
        contentElm.classList.remove('subtle-bounce-in-active')
      }, 500)
    },

    goToVenueStage () {
      this.$root.booking.venue = null
      this.$refs.venueStage.requestVenues()
        .then((venues) => {
          this.analyticsService.trackEvent('Booking Widget v2', 'Finished Loading', venues[0].name, 1)
          this.analyticsService.emitEvent('finishedLoading')
        })

      this.setStageTextForVenue()
      this.stageManager.openStage(0)
    },

    goToDateStage () {
      this.$root.booking.date = null
      this.$refs.dateStage.requestDateAvailability()

      if (!this.$refs.paxSelector.rangeManuallySet) {
        this.$refs.paxSelector.requestRange()
      }

      this.setStageTextForPeopleAndDate()
      this.stageManager.openStage(1)
    },

    goToTypeStage () {
      this.$root.booking.type = null

      // Additionally clear out pax, date and time if the flow is type first.
      if (this.showBookingTypeFirst) {
        this.$root.booking.pax = null
        this.$root.booking.date = null
        this.$root.booking.times = null
      }

      this.$refs.typeStage.requestTypes()
      this.setStageTextForType()
      this.stageManager.openStage(2)

      this.analyticsService.trackEvent('Booking Widget v2', 'Get Booking Types', this.$root.booking.venue.name, 1)
    },

    goToLinkedTypesStage () {
      this.$refs.typeStage.requestTypes()
      this.stageManager.setStageText(8, 'Select Additional Booking Types Below (Optional)')
      this.stageManager.openStage(8)

      this.analyticsService.trackEvent('Booking Widget v2', 'Suggest Linked Booking Types', this.$root.booking.venue.name, 1)
    },

    goToTimeStage () {
      this.$root.booking.times = null
      this.$refs.timeStage.requestFromTimesBatched()
      this.$refs.timeStage.clearEndTimes()
      this.setStageTextForTimes()
      this.stageManager.openStage(3)

      this.analyticsService.trackEvent('Booking Widget v2', 'Get times', this.$root.booking.venue.name, 1)
    },

    goToMultiTimeStage () {
      this.$root.booking.times = null
      this.$refs.multiTimeStage.requestTimes()
      this.stageManager.setStageText(3, this.$t('time_stage.header'))
      this.stageManager.openStage(9)

      this.analyticsService.trackEvent('Booking Widget v2', 'Get Linked Types times', this.$root.booking.venue.name, 1)
    },

    goToDetailsStage () {
      this.$refs.detailsStage.requestBookingDetails()
      this.stageManager.openStage(4)

      this.analyticsService.trackEvent('Booking Widget v2', 'Check Final Availability', this.$root.booking.venue.name, 1)
    },

    goToConfirmationStage () {
      this.stageManager.openStage(7)
    },

    openModal () {
      this.windowManager.goToCheckout(this.$root.booking, this.$root.additionalTypes)
    },

    changeDetails () {
      this.$root.booking.times = null
      this.goToTimeStage()

      this.analyticsService.trackEvent('Booking Widget v2', 'Change Details', this.$root.booking.venue.name, 1)
    },

    bookingSent (confirmedBooking) {
      this.goToConfirmationStage()
      this.$root.confirmedBooking = confirmedBooking
    },

    shouldShowPoweredBy () {
      if (this.$root.dmnOptions.from === Source.designmynight) {
        return false
      }

      return !this.$root.dmnOptions.hidePoweredBy
    },

    goToAlternativeStage () {
      this.$refs.altStage.requestAlternatives()
      this.stageManager.openStage(5)

      this.analyticsService.trackEvent('Booking Widget v2', 'Check Alternative Availability', this.$root.booking.venue.name, 1)
    },

    seeMoreAlternativesForDate (date) {
      this.$root.booking.date = date
      this.setStageTextForPeopleAndDate()
      this.goToTimeStage()
    },

    selectAlternativeStartTime (date, time) {
      // this.$root.booking.date = date
      // this.$root.booking.times.startTime = time
      // this.goToTimeStage()
      this.seeMoreAlternativesForDate(date)
    },

    goToFirstStage () {
      if (this.showBookingTypeFirst) {
        this.goToTypeStage()
      } else {
        this.goToDateStage()
      }
    },

    popupAction (action) {
      switch (action) {
        case 'showAlternatives':
          this.goToAlternativeStage()
          break
        case 'restartFlow':
          this.goToFirstStage()
      }

      this.$root.prompt.isOpen = false
    },

    tertiaryPopupAction (action) {
      switch (action) {
        case 'checkAlternativeVenues':
          this.windowManager.goToCheckout(this.$root.booking)
          break
      }

      this.$root.prompt.isOpen = false
    },

    _launch () {
      const urlOptions = {
        source: this.$root.dmnOptions.from,
        affiliateId: this.$root.dmnOptions.affiliateId,
        customSource: this.$root.dmnOptions.customSource,
        returnUrl: this.$root.dmnOptions.returnUrl,
        googleAnalyticsCode: this.$root.dmnOptions.googleAnalyticsCode,
        googleTagManagerCode: this.$root.dmnOptions.googleTagManagerCode,
        stylesheet: this.$root.dmnOptions.stylesheet,
        locale: this.$root.dmnOptions.locale
      }

      this.showBookingTypeFirst = this.$root.dmnOptions.showTypeFirst
      this.stageManager = new StageManager(this, this.showBookingTypeFirst)
      this.windowManager = new WindowManager(this, this.$root.modal, urlOptions)

      this.promptService = new PromptService(this.$root.prompt)

      this.analyticsService = new AnalyticsService(this.$root.dmnOptions.from, this.$root)
      this.gtmService = new GoogleTagManagerService()

      this.goToVenueStage()
    },

    _deferredStart (options) {
      if (options === undefined) throw new Error('No options specified')
      this.$root.dmnOptions = options
      this._launch()
    }

  },
  mounted () {
    this._launch()
  }
}



// WEBPACK FOOTER //
// ./src/assets/js/app.js